import Button from "@/components/button"
import CollapsibleCard from "@/components/collapsible-card"
import RoundedButton from "@/components/rounded-button"
import { Icon } from "@/icons/icon"
import ParamRemarksSheet from "@/ipqc/components/param-remarks-sheet"
import {
  toPathString,
  useFormEmit,
  UseFormReturn,
} from "@/lib/form/form"
import { FormContextProvider } from "@/lib/form/form-context"
import { useWidgetRegistry } from "@/lib/form/hooks"
import {
  DropdownParamSchema,
  ParamChoice,
  ParamSchema,
  ParamSchemaMode,
  ParamSchemaType,
  ParamValidationType,
  RadioParamSchema,
} from "@/lib/form/schema"
import { FormParamStatus } from "@/lib/form/types"
import ChoiceWidget from "@/lib/form/widgets/choice"
import DatePickerWidget from "@/lib/form/widgets/date-picker"
import DropdownWidget from "@/lib/form/widgets/dropdown"
import LimitWidget from "@/lib/form/widgets/limit"
import RadioGroupWidget from "@/lib/form/widgets/radio-group"
import { cn } from "@/lib/utils"
import { Schema, schemaPathToValuePath } from "@/rmpm/lib/schema"
import useStore from "@/store"
import { cloneDeep, get, set } from "lodash"
import { useRef } from "react"

export interface RmPmFormProps {
  className?: string
  schema?: Schema
  form: UseFormReturn
  editable?: boolean
}

export default function RmPmForm({
  className,
  schema,
  form,
  editable = true,
}: RmPmFormProps) {
  const registry = useWidgetRegistry()

  return (
    <FormContextProvider form={form} formSchema={{}} registry={registry}>
      <form className={cn(className)}>
        <RenderWidget schema={schema} form={form} editable={editable} />
      </form>

      <ParamRemarksSheet form={form} />
    </FormContextProvider>
  )
}

interface RenderWidgetProps {
  className?: string
  schema?: Schema
  schemaPath?: string
  form: UseFormReturn
  editable?: boolean
}

function getFormProductStatus(form: UseFormReturn, schemaPath: string) {
  const valuePath = schemaPathToValuePath(schemaPath)
  const product = form.getValues(valuePath)
  return product?.status
}

const REJECT_INTENT_KEY = "__rejectRequested"

function isProductMarkedForReject(form: UseFormReturn, schemaPath: string) {
  const valuePath = schemaPathToValuePath(schemaPath)
  const product = form.getValues(valuePath)
  return Boolean(product?.[REJECT_INTENT_KEY])
}

function isRejectedProductStatus(status?: FormParamStatus) {
  return (
    status === FormParamStatus.REJECTED_PENDING_APPROVAL ||
    status === FormParamStatus.REJECTED_APPROVED
  )
}

function disableStatusesRecursively(value: any): any {
  if (Array.isArray(value)) {
    return value.map(disableStatusesRecursively)
  }

  if (!value || typeof value !== "object") {
    return value
  }

  const nextValue: Record<string, any> = {}
  for (const [key, child] of Object.entries(value)) {
    nextValue[key] = disableStatusesRecursively(child)
  }

  if ("status" in nextValue) {
    nextValue.status = FormParamStatus.DISABLED
  }

  return nextValue
}

function markProductWithRejectIntent(
  value: any,
  previousStatus: FormParamStatus | undefined
): any {
  if (!value || typeof value !== "object") {
    return value
  }

  const product = cloneDeep(value)
  product.status = previousStatus ?? product.status
  product[REJECT_INTENT_KEY] = true

  if (product.skus && typeof product.skus === "object") {
    for (const [key, sku] of Object.entries(product.skus)) {
      if (!sku || typeof sku !== "object") {
        continue
      }

      if (Array.isArray((sku as any).parameters)) {
        ;(product.skus as Record<string, any>)[key] = {
          ...(sku as Record<string, any>),
        }
      }
    }
  }

  return product
}

function RenderWidget({
  className,
  schema,
  schemaPath = "",
  form,
  editable,
}: RenderWidgetProps) {
  const emit = useFormEmit(form)
  const rmpmStore = useStore.use.rmpm()
  const rejectSnapshotRef = useRef<Record<string, any> | null>(null)

  function checkResampleStatus(): boolean {
    const sheetId = form.getValues().sheetId
    return (
      rmpmStore.getTruckMetaSchemaByOnlyId(sheetId || "")?.isResample || false
    )
  }

  function isSubmitted(): boolean {
    const sheetId = form.getValues().sheetId
    return rmpmStore.submitted[sheetId || ""] === FormParamStatus.SUBMITTED
  }

  function rejectProduct() {
    const currentValues = cloneDeep(form.getValues())
    rejectSnapshotRef.current = currentValues
    const productPath = schemaPathToValuePath(schemaPath)
    const currentProduct = get(currentValues, productPath)
    const previousProductStatus = currentProduct?.status as
      | FormParamStatus
      | undefined

    const frozenValues = disableStatusesRecursively(currentValues)
    const frozenProduct = get(frozenValues, productPath)

    if (!frozenProduct) {
      return
    }

    set(
      frozenValues,
      productPath,
      markProductWithRejectIntent(frozenProduct, previousProductStatus)
    )

    form.setValue("", frozenValues, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
      merge: false,
    })
  }

  function undoRejectProduct() {
    if (!rejectSnapshotRef.current) {
      return
    }

    form.setValue("", cloneDeep(rejectSnapshotRef.current), {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
      merge: false,
    })

    rejectSnapshotRef.current = null
  }

  function toLimitSchema(schema: Schema): ParamSchema {
    return {
      id: schema.properties?.id.default as string,
      name: schema.options?.label || "",
      mode: schema.options?.mode || ParamSchemaMode.INFO,
      type: schema.options?.widget || ParamSchemaType.NONE,
      subtext: schema.options?.subtext,
      validationType: ParamValidationType.NONE,
      unit: schema.options?.unit || "",
    }
  }

  function toChoiceSchema(schema: Schema): ParamSchema {
    let choices: ParamChoice[] = []
    if (schema.options?.choices) {
      choices = schema.options.choices.map((choice) => ({
        id: choice.id,
        label: choice.label,
        value: choice.value,
        isCorrect: !!choice.meta?.isCorrect,
      }))
    }

    return {
      id: schema.properties?.id.default as string,
      name: schema.options?.label || "",
      mode: schema.options?.mode || ParamSchemaMode.INFO,
      type: schema.options?.widget || ParamSchemaType.NONE,
      validationType: ParamValidationType.NONE,
      unit: schema.options?.unit || "",
      choices: choices,
    }
  }

  function toDropdownSchema(schema: Schema): DropdownParamSchema {
    return {
      id: (schema.properties?.id?.default as string) || "",
      name: schema.options?.label || "",
      mode: schema.options?.mode || ParamSchemaMode.INFO,
      type: ParamSchemaType.DROPDOWN,
      options: schema.options?.choices || [],
    }
  }

  function toRadioParamSchema(schema: Schema): RadioParamSchema {
    return {
      id: (schema.properties?.id.default as string) || "",
      name: schema.options?.label || "",
      mode: schema.options?.mode || ParamSchemaMode.INFO,
      type: ParamSchemaType.RADIO,
      options: schema.options?.choices || [],
    }
  }

  function handleClickAdd() {
    emit("clickAdd", {
      path: schemaPath,
      schema: schema,
      payload: form.getValues(schemaPathToValuePath(schemaPath)),
    })
  }

  function handleClickDelete() {
    emit("clickDelete", {
      path: schemaPath,
      schema: schema,
      payload: form.getValues(schemaPathToValuePath(schemaPath)),
    })
  }

  if (!schema || !schema.type) {
    return false
  } else if (schema.options?.widget === ParamSchemaType.PLAIN) {
    return (
      <div className={cn("mb-4", className)}>
        <div className="pt-5">
          {schema.type === "object" &&
            schema.properties &&
            Object.entries(schema.properties).map(([key, prop]) => (
              <RenderWidget
                key={key}
                schemaPath={toPathString([schemaPath, "properties", key])}
                schema={prop}
                form={form}
                editable={editable}
              />
            ))}

          {schema.type === "array" &&
            schema.items &&
            schema.items.map((item, index) => (
              <RenderWidget
                key={`${item.id || item.parameterId || "item"}-${index}`}
                schemaPath={toPathString([schemaPath, "items", `${index}`])}
                schema={item}
                form={form}
                editable={editable}
              />
            ))}

          {editable && schema.options?.addable && !schemaPath.endsWith("skus") && (
            <RoundedButton
              className="ml-auto"
              icon={<Icon name="Plus" />}
              onClick={handleClickAdd}
            >
              {schema.options.label}
            </RoundedButton>
          )}
        </div>
      </div>
    )
  } else if (schema.options?.widget === ParamSchemaType.SECTION) {
    return (
      <SectionWidget
        schema={schema}
        schemaPath={schemaPath}
        form={form}
        editable={editable}
        onClickAdd={handleClickAdd}
      />
    )
  } else if (schema.options?.widget === ParamSchemaType.CARD_SECTION) {
    return (
      <CardSectionWidget
        schema={schema}
        schemaPath={schemaPath}
        form={form}
        editable={editable}
        onClickAdd={handleClickAdd}
        onClickDelete={handleClickDelete}
      />
    )
  } else if (schema.options?.widget === ParamSchemaType.CARD) {
    const currentProductStatus = getFormProductStatus(form, schemaPath)
    const isProductRejected =
      isRejectedProductStatus(currentProductStatus) ||
      isProductMarkedForReject(form, schemaPath)

    return (
      <div className={cn("mb-4 px-4", className)}>
        <CollapsibleCard
          title={schema.options.label}
          hideTitle={schema.options.hideLabel}
          actions={
            isSubmitted() &&
            isProductRejected ? (
              <div className="flex items-center justify-end">
                <button className="border border-red-500 text-red-500 px-3 py-1 rounded-sm text-sm font-medium mr-[0.5em]">
                  Rejected
                </button>
              </div>
            ) : (
              editable &&
              schema.options.removable &&
              schema.id && (
                <div className="flex items-center justify-end">
                  <Button variant="ghost" onClick={handleClickDelete}>
                    <Icon className="h-6 w-auto text-icon" name="TrashCan" />
                  </Button>
                </div>
              )
            )
          }
        >
          <div className="px-3 pt-5">
            {schema.type === "object" &&
              schema.properties &&
              Object.entries(schema.properties).map(([key, prop]) => (
                <RenderWidget
                  key={key}
                  schemaPath={toPathString([schemaPath, "properties", key])}
                  schema={prop}
                  form={form}
                  editable={editable}
                />
              ))}

            {schema.type === "array" &&
              schema.items &&
              schema.items.map((item, index) => (
                <RenderWidget
                  key={`${item.id || item.parameterId || "item"}-${index}`}
                  schemaPath={toPathString([schemaPath, "items", `${index}`])}
                  schema={item}
                  form={form}
                  editable={editable}
                />
              ))}
          </div>

          {/* --- Reject Product Button --- */}
          {schema.id &&
            !checkResampleStatus() &&
            !isSubmitted() &&
            !isProductRejected &&
            currentProductStatus !==
              FormParamStatus.SUBMITTED &&
            currentProductStatus !== FormParamStatus.DISABLED && (
              <div className="px-3 pt-4">
                <button
                  type="button"
                  onClick={rejectProduct}
                  className="mt-3 text-600 hBlue font-semibold text-sm cursor-pointer flex items-center gap-1 transition"
                >
                  <Icon name="Close" height={"1.35rem"} width={"1.35rem"} />
                  Reject Product
                </button>
              </div>
            )}
          {schema.id &&
            !isSubmitted() &&
            isProductRejected && (
              <div className="px-3 pt-4 flex items-center justify-between">
                {!isRejectedProductStatus(currentProductStatus) &&
                  !!rejectSnapshotRef.current && (
                    <button
                      type="button"
                      onClick={undoRejectProduct}
                      className="mt-3 text-600 hBlue font-semibold text-sm cursor-pointer flex items-center gap-1 transition"
                    >
                      <Icon name="Close" height={"1.35rem"} width={"1.35rem"} />
                      Undo Reject
                    </button>
                  )}
                <button className="border border-red-500 text-red-500 px-3 py-1 rounded-sm text-sm font-medium mr-[0.5em]">
                  Rejected
                </button>
              </div>
            )}
        </CollapsibleCard>
      </div>
    )
  } else if (
    schema.options?.widget === ParamSchemaType.VALUE_NUMBER ||
    schema.options?.widget === ParamSchemaType.VALUE_TEXT
  ) {
    return (
      <div className="mb-4">
        <LimitWidget
          path={schemaPathToValuePath(schemaPath)}
          schema={toLimitSchema(schema)}
        />

        {schema.type === "object" &&
          schema.properties &&
          Object.entries(schema.properties).map(([key, prop]) => (
            <RenderWidget
              key={key}
              schemaPath={toPathString([schemaPath, "properties", key])}
              schema={prop}
              form={form}
              editable={editable}
            />
          ))}

        {schema.type === "array" &&
          schema.items &&
          schema.items.map((item, index) => (
            <RenderWidget
              key={`${item.id || item.parameterId || "item"}-${index}`}
              schemaPath={toPathString([schemaPath, "items", `${index}`])}
              schema={item}
              form={form}
              editable={editable}
            />
          ))}
      </div>
    )
  } else if (schema.options?.widget === ParamSchemaType.CHOICE) {
    return (
      <>
        <ChoiceWidget
          className="mb-4"
          path={schemaPathToValuePath(schemaPath)}
          schema={toChoiceSchema(schema)}
        />

        {schema.type === "object" &&
          schema.properties &&
          Object.entries(schema.properties).map(([key, prop]) => (
            <RenderWidget
              key={key}
              schemaPath={toPathString([schemaPath, "properties", key])}
              schema={prop}
              form={form}
              editable={editable}
            />
          ))}

        {schema.type === "array" &&
          schema.items &&
          schema.items.map((item, index) => (
            <RenderWidget
              key={`${item.id || item.parameterId || "item"}-${index}`}
              schemaPath={toPathString([schemaPath, "items", `${index}`])}
              schema={item}
              form={form}
              editable={editable}
            />
          ))}
      </>
    )
  } else if (schema.options?.widget === ParamSchemaType.RADIO) {
    return (
      <>
        <RadioGroupWidget
          className="mb-4"
          path={schemaPathToValuePath(schemaPath)}
          schema={toRadioParamSchema(schema)}
        />

        {schema.type === "object" &&
          schema.properties &&
          Object.entries(schema.properties).map(([key, prop]) => (
            <RenderWidget
              key={key}
              schemaPath={toPathString([schemaPath, "properties", key])}
              schema={prop}
              form={form}
              editable={editable}
            />
          ))}

        {schema.type === "array" &&
          schema.items &&
          schema.items.map((item, index) => (
            <RenderWidget
              key={`${item.id || item.parameterId || "item"}-${index}`}
              schemaPath={toPathString([schemaPath, "items", `${index}`])}
              schema={item}
              form={form}
              editable={editable}
            />
          ))}
      </>
    )
  } else if (schema.options?.widget === ParamSchemaType.DROPDOWN) {
    return (
      <>
        <DropdownWidget
          className="mb-4"
          path={schemaPathToValuePath(schemaPath)}
          schema={toDropdownSchema(schema)}
        />

        {schema.type === "object" &&
          schema.properties &&
          Object.entries(schema.properties).map(([key, prop]) => (
            <RenderWidget
              key={key}
              schemaPath={toPathString([schemaPath, "properties", key])}
              schema={prop}
              form={form}
              editable={editable}
            />
          ))}

        {schema.type === "array" &&
          schema.items &&
          schema.items.map((item, index) => (
            <RenderWidget
              key={`${item.id || item.parameterId || "item"}-${index}`}
              schemaPath={toPathString([schemaPath, "items", `${index}`])}
              schema={item}
              form={form}
              editable={editable}
            />
          ))}
      </>
    )
  } else if (schema.options?.widget === ParamSchemaType.DATE) {
    return (
      <>
        <DatePickerWidget
          className="mb-4"
          path={schemaPathToValuePath(schemaPath)}
          schema={toLimitSchema(schema)}
        />

        {schema.type === "object" &&
          schema.properties &&
          Object.entries(schema.properties).map(([key, prop]) => (
            <RenderWidget
              key={key}
              schemaPath={toPathString([schemaPath, "properties", key])}
              schema={prop}
              form={form}
              editable={editable}
            />
          ))}

        {schema.type === "array" &&
          schema.items &&
          schema.items.map((item, index) => (
            <RenderWidget
              key={`${item.id || item.parameterId || "item"}-${index}`}
              schemaPath={toPathString([schemaPath, "items", `${index}`])}
              schema={item}
              form={form}
              editable={editable}
            />
          ))}
      </>
    )
  } else if (schema.type === "object" && schema.properties) {
    return Object.entries(schema.properties).map(([key, prop]) => (
      <RenderWidget
        key={key}
        schemaPath={toPathString([schemaPath, "properties", key])}
        schema={prop}
        form={form}
        editable={editable}
      />
    ))
  } else if (schema.type === "array" && schema.items) {
    return schema.items.map((item, index) => (
      <RenderWidget
        key={`${item.id || item.parameterId || "item"}-${index}`}
        schemaPath={toPathString([schemaPath, "items", `${index}`])}
        schema={item}
        form={form}
        editable={editable}
      />
    ))
  } else {
    return false
  }
}

interface SectionWidgetProps {
  schema: Schema
  schemaPath: string
  form: UseFormReturn
  editable?: boolean
  onClickAdd?: () => void
}

function SectionWidget({
  schema,
  schemaPath,
  form,
  editable,
  onClickAdd,
}: SectionWidgetProps) {
  return (
    <div>
      <h3
        className={cn(
          `
            flex justify-between items-center py-2.5 px-4
            bg-band-fill uppercase text-sm font-semibold
          `,
          schema.options?.hideLabel && "sr-only"
        )}
      >
        {schema.options?.label}

        {editable && schema.options?.addable && !schemaPath.endsWith("skus") && (
          <RoundedButton icon={<Icon name="Plus" />} onClick={onClickAdd}>
            {schema.options?.label}
          </RoundedButton>
        )}
      </h3>

      <div className="px-4 pt-4">
        {schema.type === "object" &&
          schema.properties &&
          Object.entries(schema.properties).map(([key, prop]) => (
            <RenderWidget
              key={key}
              className="px-0"
              schemaPath={toPathString([schemaPath, "properties", key])}
              schema={prop}
              form={form}
              editable={editable}
            />
          ))}

        {schema.type === "array" &&
          schema.items &&
          schema.items.map((item, index) => (
            <RenderWidget
              key={`${item.id || item.parameterId || "item"}-${index}`}
              className="px-0"
              schemaPath={toPathString([schemaPath, "items", `${index}`])}
              schema={item}
              form={form}
              editable={editable}
            />
          ))}
      </div>
    </div>
  )
}

interface CardSectionWidgetProps {
  schema: Schema
  schemaPath: string
  form: UseFormReturn
  editable?: boolean
  onClickAdd?: () => void
  onClickDelete?: () => void
}

function CardSectionWidget({
  schema,
  schemaPath,
  form,
  editable,
  onClickAdd,
  onClickDelete,
}: CardSectionWidgetProps) {
  return (
    <div className="mb-4 bg-white rounded-lg border overflow-hidden">
      {/* Card Header with title and delete button */}
      <div className="flex justify-between items-center py-3 px-4 border-b">
        <span className="font-semibold text-base">{schema.options?.label}</span>
        {editable && schema.options?.removable && schema.id && (
          <button
            type="button"
            onClick={onClickDelete}
            className="text-gray-400 hover:text-red-500"
          >
            <Icon className="h-5 w-5" name="TrashCan" />
          </button>
        )}
      </div>

      {/* Card Content */}
      <div className="p-4">
        {schema.type === "object" &&
          schema.properties &&
          Object.entries(schema.properties).map(([key, prop]) => (
            <RenderWidget
              key={key}
              className="px-0"
              schemaPath={toPathString([schemaPath, "properties", key])}
              schema={prop}
              form={form}
              editable={editable}
            />
          ))}

        {schema.type === "array" &&
          schema.items &&
          schema.items.map((item, index) => (
            <RenderWidget
              key={`${item.id || item.parameterId || "item"}-${index}`}
              className="px-0"
              schemaPath={toPathString([schemaPath, "items", `${index}`])}
              schema={item}
              editable={editable}
              form={form}
            />
          ))}
      </div>

      {editable && schema.options?.addable && !schemaPath.endsWith("skus") && (
        <RoundedButton
          className="ml-auto"
          icon={<Icon name="Plus" />}
          onClick={onClickAdd}
        >
          {schema.options?.label}
        </RoundedButton>
      )}
    </div>
  )
}
